import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  Field,
  Input,
} from '@aeris/ui'

import { createPlatformAccount } from '../lib/api/platform.ts'
import { ApiError } from '../lib/api.ts'

import { CredentialHandover } from './credential-handover.tsx'

/**
 * Abrir a loja de um cliente (F119).
 *
 * Dois estados no mesmo diálogo — o formulário e a entrega —, e a entrega não é
 * um aviso passageiro: a senha que está nela não existe em lugar nenhum. O
 * servidor a cifrou e esqueceu, exatamente como faz com a senha que alguém
 * escolhe. Perdê-la significa redefinir, não consultar, e uma interface que a
 * deixa sumir com um clique fora é uma interface que produz essa redefinição.
 *
 * O mesmo molde de `member-form.tsx`, e de propósito: quem já entregou uma
 * senha pela tela de Equipe reconhece esta. O que muda é o que nasce junto —
 * ali uma pessoa, aqui uma empresa inteira.
 */

const schema = z.object({
  legalName: z.string().trim().min(1, 'Informe a razão social').max(200),
  tradeName: z.string().trim().max(200),
  name: z.string().trim().min(1, 'Informe o nome da conta').max(200),
  slug: z
    .string()
    .trim()
    .min(1, 'Informe o identificador')
    .refine(
      (value) => /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/.test(value),
      'Use letras minúsculas, números e hífen'
    ),
  cnpj: z.string().trim().min(1, 'Informe o CNPJ'),
  ownerName: z.string().trim().min(1, 'Informe o nome do dono').max(200),
  ownerEmail: z
    .string()
    .trim()
    .min(1, 'Informe o e-mail do dono')
    .max(320)
    .refine(
      (value) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value),
      'Informe um e-mail válido'
    ),
})

type FormValues = z.infer<typeof schema>

/**
 * Um slug a partir do nome, como sugestão e nunca como imposição.
 *
 * Ele não muda depois de a conta existir — nada no produto renomeia um slug —,
 * então sugerir é ajuda e decidir seria armadilha.
 */
const slugify = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)

export type PlatformAccountFormProps = {
  onClose: () => void
}

export const PlatformAccountForm = ({
  onClose,
}: PlatformAccountFormProps): ReactNode => {
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [handover, setHandover] = useState<{
    accountName: string
    email: string
    password: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      legalName: '',
      tradeName: '',
      name: '',
      slug: '',
      cnpj: '',
      ownerName: '',
      ownerEmail: '',
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: FormValues) =>
      await createPlatformAccount({
        slug: values.slug,
        name: values.name,
        legalName: values.legalName,
        ...(values.tradeName === '' ? {} : { tradeName: values.tradeName }),
        cnpj: values.cnpj,
        ownerName: values.ownerName,
        ownerEmail: values.ownerEmail,
      }),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['platform-accounts'] })

      if (response.temporaryPassword === null) {
        // O e-mail já tinha conta no Aeris: essa pessoa fica com a senha que
        // escolheu, e não há o que entregar.
        onClose()
        return
      }

      setHandover({
        accountName: response.account.name,
        email: response.account.ownerEmail ?? '',
        password: response.temporaryPassword,
      })
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível abrir a conta. Tente novamente.'
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    // Um envio por vez (F248): o segundo, com o primeiro andando, criaria outro.
    if (mutation.isPending) return
    setFormError(null)
    mutation.mutate(values)
  })

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        // Enquanto a senha está na tela, a única saída é o botão que a
        // reconhece.
        if (!next && handover === null) onClose()
      }}
    >
      <DialogContent
        title={handover === null ? 'Abrir conta' : 'Senha temporária'}
        description={
          handover === null
            ? 'A conta nasce com matriz, cliente de balcão e tabela de preço, pronta para vender.'
            : 'Anote agora: ela não será mostrada de novo.'
        }
        className="max-w-xl"
      >
        {handover === null ? (
          <form
            onSubmit={(event) => {
              void onSubmit(event)
            }}
            noValidate
            className="flex flex-col gap-4"
          >
            {formError === null ? null : (
              <p
                role="alert"
                className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
              >
                {formError}
              </p>
            )}

            <Field
              label="Razão social"
              required
              error={errors.legalName?.message}
              hint="Como está no cartão CNPJ — é o que sai na nota"
            >
              {(field) => (
                <Input
                  {...field}
                  {...register('legalName')}
                  autoFocus
                  placeholder="68.863.686 Eduardo Lima Junior"
                />
              )}
            </Field>

            <Field label="Nome fantasia" error={errors.tradeName?.message}>
              {(field) => (
                <Input
                  {...field}
                  {...register('tradeName')}
                  placeholder="Aeris 3D"
                />
              )}
            </Field>

            <Field
              label="Nome da conta"
              required
              error={errors.name?.message}
              hint="O nome curto que aparece na tela"
            >
              {(field) => (
                <Input
                  {...field}
                  {...register('name', {
                    // O slug segue o nome enquanto ninguém o editou à mão. Um
                    // campo que se sobrescreve depois de digitado é a forma
                    // mais rápida de perder o que a pessoa escreveu.
                    onBlur: () => {
                      if (getValues('slug') === '') {
                        setValue('slug', slugify(getValues('name')), {
                          shouldValidate: true,
                        })
                      }
                    },
                  })}
                  placeholder="Aeris 3D"
                />
              )}
            </Field>

            <Field
              label="Identificador"
              required
              error={errors.slug?.message}
              hint="Minúsculas, números e hífen. Não muda depois."
            >
              {(field) => (
                <Input {...field} {...register('slug')} placeholder="aeris3d" />
              )}
            </Field>

            <Field
              label="CNPJ"
              required
              error={errors.cnpj?.message}
              hint="Conferido com dígito verificador pelo servidor"
            >
              {(field) => (
                <Input
                  {...field}
                  {...register('cnpj')}
                  inputMode="text"
                  placeholder="68.863.686/0001-88"
                />
              )}
            </Field>

            <Field
              label="Nome do dono"
              required
              error={errors.ownerName?.message}
            >
              {(field) => (
                <Input
                  {...field}
                  {...register('ownerName')}
                  placeholder="Eduardo Lima Junior"
                />
              )}
            </Field>

            <Field
              label="E-mail do dono"
              required
              error={errors.ownerEmail?.message}
              hint="Será o login dele. Se já tiver conta no Aeris, ele entra com a senha que já usa."
            >
              {(field) => (
                <Input
                  {...field}
                  {...register('ownerEmail')}
                  type="email"
                  autoComplete="off"
                  placeholder="eduardo@aeris3d.com.br"
                />
              )}
            </Field>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                variant="primary"
                loading={mutation.isPending}
              >
                Abrir conta
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <CredentialHandover
            intro={`Entregue-a a quem vai abrir a ${handover.accountName}.`}
            email={handover.email}
            password={handover.password}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
